import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Camera } from 'lucide-react';
import { useCreateSnapshot } from '@/service/gpu-instance/volume-hook';
import type { Volume } from '@/service/gpu-instance/volume-type';
import { toast } from 'sonner';

interface CreateSnapshotDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  volume: Volume | null;
  onSuccess?: () => void;
}

export const CreateSnapshotDialog = ({
  open,
  onOpenChange,
  volume,
  onSuccess,
}: CreateSnapshotDialogProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const createSnapshot = useCreateSnapshot();

  if (!volume) return null;

  const validateName = (value: string) => {
    return /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/.test(value);
  };

  const isValid = name.length >= 1 && validateName(name);

  const handleCreate = async () => {
    if (!isValid) return;

    try {
      await createSnapshot.mutateAsync({
        volumeId: volume.id,
        name,
        description: description || undefined,
      });
      toast.success('Snapshot creation started', {
        description: `Creating snapshot "${name}" of volume "${volume.name}".`,
      });
      onOpenChange(false);
      onSuccess?.();
      // Reset form
      setName('');
      setDescription('');
    } catch {
      toast.error('Failed to create snapshot');
    }
  };

  const suggestedName = `${volume.name}-snap-${new Date().toISOString().slice(0, 10)}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Create Snapshot
          </DialogTitle>
          <DialogDescription>
            Create a point-in-time snapshot of "{volume.name}".
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Volume Info */}
          <div className="rounded-lg bg-muted/50 p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Volume</span>
              <span className="font-medium">{volume.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Current Size</span>
              <span>{volume.usedSize} GB used of {volume.requestedSize} GB</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Existing Snapshots</span>
              <span>{volume.snapshotCount}</span>
            </div>
          </div>

          {/* Snapshot Name */}
          <div className="space-y-2">
            <Label htmlFor="snapshot-name">Snapshot Name</Label>
            <Input
              id="snapshot-name"
              placeholder={suggestedName}
              value={name}
              onChange={(e) => setName(e.target.value.toLowerCase())}
              className={name && !validateName(name) ? 'border-destructive' : ''}
            />
            <p className="text-xs text-muted-foreground">
              Lowercase letters, numbers, and dashes only.
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              placeholder="What is this snapshot for?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!isValid || createSnapshot.isPending}>
            {createSnapshot.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Snapshot
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateSnapshotDialog;
